import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Leaf,
  MapPin,
  ShieldCheck,
  X,
  Minus,
  Plus,
  HelpCircle,
  Clock,
  Flame,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Tag,
  Percent,
  Wallet,
  Sparkles,
  Droplets,
  Wind,
  Activity,
  HeartHandshake,
  UserCheck,
  Image as ImageIcon,
  Video,
  Play,
  FileText,
  Download,
  Eye,
  UploadCloud,
  Trash2,
  Maximize2,
  ExternalLink,
  File as FileIcon,
  Check,
  AlertCircle,
  Film,
  Share2,
  Heart,
  ArrowRight,
  Ticket,
} from "lucide-react";
import { Helmet } from "react-helmet";
import toast, { Toaster } from "react-hot-toast";
import {
  fetchPublicTreatmentRetreatPackageById,
  fetchPublicTreatmentRetreatPackages,
  fetchApplicableCommission,
  type PublicTreatmentRetreatPackage,
  type PublicGalleryItem,
} from "./treatmentRetreatPublicApi";

const toArray = <T,>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];

const formatCurrency = (amount?: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const formatOnwardsPrice = (val: number | string) => {
  const num = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9]/g, '')) || 0;
  return `₹${num.toLocaleString('en-IN')}`;
};

const formatInlineMarkdown = (content: string) => {
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-[#15302B]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const renderDescriptionContent = (text: string) => {
  if (!text || !text.trim()) return null;

  // If already contains HTML markup from rich text editor
  const isHtml = /<[a-z][\s\S]*>/i.test(text);
  if (isHtml) {
    return (
      <div
        className="space-y-2.5 text-sm leading-relaxed text-[#4A5568] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:text-[#15302B] [&_strong]:font-semibold"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }

  // Parse markdown bold, bullet points, and paragraphs
  const paragraphs = text.split(/\r?\n\r?\n/).filter(Boolean);

  return (
    <div className="space-y-3 text-sm leading-relaxed text-[#4A5568]">
      {paragraphs.map((para, pIdx) => {
        const lines = para.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        const isBulletList = lines.length > 0 && lines.every((line) => line.startsWith("- ") || line.startsWith("* ") || line.startsWith("• "));

        if (isBulletList) {
          return (
            <ul key={pIdx} className="space-y-1.5 pl-5 list-disc text-[#4A5568]">
              {lines.map((line, lIdx) => {
                const bulletText = line.replace(/^[-*•]\s+/, "");
                return <li key={lIdx}>{formatInlineMarkdown(bulletText)}</li>;
              })}
            </ul>
          );
        }

        return (
          <p key={pIdx} className="leading-relaxed">
            {lines.map((line, lIdx) => (
              <React.Fragment key={lIdx}>
                {formatInlineMarkdown(line)}
                {lIdx < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

const WellnessAndNaturopathyDetailPage = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();

  const [packageData, setPackageData] =
    useState<PublicTreatmentRetreatPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [commissionPercentage, setCommissionPercentage] = useState<number | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedQuantities, setSelectedQuantities] = useState<
    Record<string, number>
  >({});
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [isImageLightboxOpen, setIsImageLightboxOpen] = useState(false);
  const [isWishlistActive, setIsWishlistActive] = useState(false);
  const [isIncludedOpen, setIsIncludedOpen] = useState(false);
  const [isNotIncludedOpen, setIsNotIncludedOpen] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [isFaqSectionOpen, setIsFaqSectionOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(true);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [otherPackages, setOtherPackages] = useState<PublicTreatmentRetreatPackage[]>([]);

  // Gallery state
  const [galleryItems, setGalleryItems] = useState<PublicGalleryItem[]>([]);
  const [activeGalleryTab, setActiveGalleryTab] = useState<"all" | "photo" | "video" | "pdf">("all");
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [activeVideo, setActiveVideo] = useState<PublicGalleryItem | null>(null);
  const [activePdf, setActivePdf] = useState<PublicGalleryItem | null>(null);
  // Sync and display ONLY authentic media uploaded/added from Admin Panel
  useEffect(() => {
    if (!packageId) return;

    // Clear legacy mock storage if previously stored in browser
    try {
      localStorage.removeItem(`metafit_gallery_${packageId}`);
    } catch (e) {
      // ignore
    }

    const backendGallery: PublicGalleryItem[] = toArray(packageData?.gallery);

    const combined: PublicGalleryItem[] = [];
    const seenUrls = new Set<string>();

    // Only actual media from Admin panel Experience Gallery (Photos, Videos, PDFs)
    backendGallery.forEach((item) => {
      if (item && item.url && !seenUrls.has(item.url)) {
        seenUrls.add(item.url);
        combined.push(item);
      }
    });

    if (combined.length === 0 && packageData?.uploadImages) {
      const photos = toArray(packageData.uploadImages);
      photos.forEach((imgUrl, idx) => {
        if (typeof imgUrl === "string" && imgUrl.trim() && !seenUrls.has(imgUrl)) {
          seenUrls.add(imgUrl);
          combined.push({
            id: `upload-${idx}`,
            type: "photo",
            url: imgUrl,
            title: `Photo ${idx + 1}`,
          });
        }
      });
    }

    setGalleryItems(combined);
  }, [packageData, packageId]);

  useEffect(() => {
    const loadDetails = async () => {
      if (!packageId) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetchPublicTreatmentRetreatPackageById(packageId);
        if (response?.success && response.data) {
          setPackageData(response.data);
          // Fetch applicable commission percentage dynamically from backend Commission Settings
          try {
            const commRate = await fetchApplicableCommission({
              serviceId: response.data._id,
              vendorId: (response.data as any).mentorId,
            });
            setCommissionPercentage(commRate);
          } catch (commErr) {
            console.error("Failed to load applicable commission:", commErr);
            setCommissionPercentage(null);
          }
        } else {
          setPackageData(null);
          setCommissionPercentage(null);
        }
      } catch (err) {
        console.error("Error loading package details:", err);
        setPackageData(null);
        setCommissionPercentage(null);
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [packageId]);

  useEffect(() => {
    const loadOtherPackages = async () => {
      try {
        const response = await fetchPublicTreatmentRetreatPackages();
        if (response?.success && Array.isArray(response.data)) {
          setOtherPackages(response.data.filter((p) => p._id !== packageId));
        }
      } catch (err) {
        console.error("Failed to load other packages:", err);
      }
    };
    loadOtherPackages();
  }, [packageId]);

  const initializeQuantities = (pkg: PublicTreatmentRetreatPackage) => {
    const initialRecord: Record<string, number> = {};
    const options = toArray(pkg.pricingOptions);
    if (options.length > 0) {
      options.forEach((option) => {
        initialRecord[option.type] = 0;
      });
      initialRecord[options[0].type] = 1;
    } else {
      initialRecord["Standard"] = 1;
    }
    return initialRecord;
  };

  const getBookingBaseTotal = (
    pkg: PublicTreatmentRetreatPackage | null,
    quantities: Record<string, number>
  ) => {
    if (!pkg) return 0;
    const options = toArray(pkg.pricingOptions);
    if (options.length > 0) {
      return options.reduce((sum, option) => {
        const qty = quantities[option.type] ?? 0;
        return sum + option.price * qty;
      }, 0);
    }
    const defaultQty = quantities["Standard"] ?? 1;
    return (pkg.price || 3500) * defaultQty;
  };

  const getCommissionRate = (pkg: PublicTreatmentRetreatPackage | null) => {
    if (commissionPercentage !== null && commissionPercentage !== undefined && !isNaN(Number(commissionPercentage))) {
      return Number(commissionPercentage);
    }
    if (pkg?.commissionPercentage !== null && pkg?.commissionPercentage !== undefined && !isNaN(Number(pkg.commissionPercentage))) {
      return Number(pkg.commissionPercentage);
    }
    const optComm = (toArray(pkg?.pricingOptions)[0] as any)?.commissionPercentage;
    if (optComm !== null && optComm !== undefined && !isNaN(Number(optComm))) {
      return Number(optComm);
    }
    return 0;
  };

  const getBookingCommissionAmount = (
    pkg: PublicTreatmentRetreatPackage | null,
    quantities: Record<string, number>
  ) => {
    const base = getBookingBaseTotal(pkg, quantities);
    const rate = getCommissionRate(pkg);
    if (rate > 0 && base > 0) {
      return Math.round(((base * rate) / 100) * 100) / 100;
    }
    return 0;
  };

  const getBookingTotal = (
    pkg: PublicTreatmentRetreatPackage | null,
    quantities: Record<string, number>
  ) => {
    const base = getBookingBaseTotal(pkg, quantities);
    const commFee = getBookingCommissionAmount(pkg, quantities);
    return Math.round((base + commFee) * 100) / 100;
  };

  const openBookingModal = () => {
    if (!packageData) return;
    const targetId = packageData._id || packageId;
    navigate(`/luxury-wellness-retreats/${targetId}/book`, {
      state: {
        packageData: packageData,
        commissionPercentage: commissionPercentage,
      },
    });
  };

  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
  };

  const updateQuantity = (type: string, delta: number) => {
    setSelectedQuantities((prev) => {
      const nextQuantity = Math.max(0, (prev[type] ?? 0) + delta);
      return {
        ...prev,
        [type]: nextQuantity,
      };
    });
  };

  const handleContinueToPreview = () => {
    if (!packageData) return;
    const baseTotal = getBookingBaseTotal(packageData, selectedQuantities);
    if (baseTotal <= 0) return;
    const rate = getCommissionRate(packageData);
    const commFee = getBookingCommissionAmount(packageData, selectedQuantities);
    const finalTotal = Math.round((baseTotal + commFee) * 100) / 100;

    navigate("/luxury-wellness-retreats/booking-preview", {
      state: {
        packageData: packageData,
        selectedQuantities,
        baseAmount: baseTotal,
        commissionPercentage: rate,
        commissionAmount: commFee,
        totalAmount: finalTotal,
      },
    });
    closeBookingModal();
  };



  // Filtered gallery items
  const filteredGallery = useMemo(() => {
    if (activeGalleryTab === "all") return galleryItems;
    return galleryItems.filter((item) => item.type === activeGalleryTab);
  }, [galleryItems, activeGalleryTab]);

  const photoCount = useMemo(() => galleryItems.filter((i) => i.type === "photo").length, [galleryItems]);
  const videoCount = useMemo(() => galleryItems.filter((i) => i.type === "video").length, [galleryItems]);
  const pdfCount = useMemo(() => galleryItems.filter((i) => i.type === "pdf").length, [galleryItems]);
  const photoItemsOnly = useMemo(() => galleryItems.filter((i) => i.type === "photo"), [galleryItems]);

  // Gallery 1-row buttery-smooth continuous auto-sliding (no pausing / no stopping)
  const galleryScrollRef = useRef<HTMLDivElement>(null);
  const [isGalleryHovered, setIsGalleryHovered] = useState(false);
  const scrollDirectionRef = useRef<"forward" | "backward">("forward");

  useEffect(() => {
    if (!isGalleryOpen || isGalleryHovered || filteredGallery.length <= 1) return;

    let animationFrameId: number;
    const speed = 0.75; // pixels per frame for buttery smooth, continuous sliding

    const step = () => {
      const container = galleryScrollRef.current;
      if (container) {
        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        if (maxScrollLeft > 0) {
          if (scrollDirectionRef.current === "forward") {
            if (container.scrollLeft >= maxScrollLeft - 2) {
              scrollDirectionRef.current = "backward";
            } else {
              container.scrollLeft += speed;
            }
          } else {
            if (container.scrollLeft <= 2) {
              scrollDirectionRef.current = "forward";
            } else {
              container.scrollLeft -= speed;
            }
          }
        }
      }
      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isGalleryOpen, isGalleryHovered, filteredGallery.length]);

  const scrollGallery = (direction: "left" | "right") => {
    if (galleryScrollRef.current) {
      scrollDirectionRef.current = direction === "left" ? "backward" : "forward";
      const scrollAmount = direction === "left" ? -240 : 240;
      galleryScrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Dynamic tags for the banner pill list
  const tagsList = useMemo(() => {
    const list: string[] = [];
    if (packageData?.category) list.push(packageData.category);
    if (packageData?.subCategory && !list.includes(packageData.subCategory)) list.push(packageData.subCategory);
    if (packageData?.treatmentType && !list.includes(packageData.treatmentType)) list.push(packageData.treatmentType);
    if (packageData?.specialization && !list.includes(packageData.specialization)) list.push(packageData.specialization);
    if (packageData?.tags) {
      packageData.tags.split(",").forEach((t) => {
        const clean = t.trim();
        if (clean && !list.includes(clean)) list.push(clean);
      });
    }
    if (list.length === 0) {
      list.push("Wellness Retreat", "Ayurveda", "Detox");
    }
    return list;
  }, [packageData]);

  // Flyer / backend media (Image 3 - ONLY this image, no extra thumbnails)
  const heroImage = useMemo(() => {
    const photos = toArray(packageData?.uploadImages);
    return (
      photos[0] ||
      "https://res.cloudinary.com/dfppeybmu/image/upload/v1791970801/treatment-retreats/zfg1kvmxlryxu5b0ejlp.jpg"
    );
  }, [packageData?.uploadImages]);

  // Clean title for display
  const cleanTitle = useMemo(() => {
    if (!packageData?.packageName) return "Full Detox & Wellness Package";
    const name = packageData.packageName;
    if (/full detox & wellness package/i.test(name)) {
      return "Full Detox & Wellness Package";
    }
    return (
      name
        .replace(/^Great!.*?(₹[\d,]+\s*)?/i, "")
        .replace(/\s*mein$/i, "")
        .trim() || name
    );
  }, [packageData?.packageName]);

  // Subtitle
  const subtitle = useMemo(() => {
    const org = packageData?.TreatmentOrganiser?.trim();
    const loc = [packageData?.city, packageData?.state].filter(Boolean).join(", ");
    if (org && loc) return `A complete body–mind reset with ancient therapies and modern wellness care at ${org}, ${loc}.`;
    if (org) return `A complete body–mind reset with ancient therapies and modern wellness care at ${org}.`;
    if (loc) return `A complete body–mind reset with ancient therapies and modern wellness care in ${loc}.`;
    return "A complete body–mind reset with ancient therapies and modern wellness care.";
  }, [packageData?.TreatmentOrganiser, packageData?.city, packageData?.state]);

  // Dynamic OpenGraph / Social preview metadata
  const pageMetaTitle = useMemo(() => {
    return `${cleanTitle} | MetaFit Luxury Wellness Retreat`;
  }, [cleanTitle]);

  const metaDescription = useMemo(() => {
    if (packageData?.description && packageData.description.trim()) {
      return packageData.description.replace(/<[^>]*>?/gm, "").substring(0, 160).trim();
    }
    return subtitle;
  }, [packageData?.description, subtitle]);

  const pageUrl = useMemo(() => {
    return typeof window !== "undefined"
      ? window.location.href
      : `https://www.metafitwellness.com/luxury-wellness-retreats/${packageId || ""}`;
  }, [packageId]);

  // Dynamically set OpenGraph and Twitter card meta tags in DOM for link preview scrapers
  useEffect(() => {
    if (!packageData) return;
    document.title = pageMetaTitle;

    const setMeta = (attr: "property" | "name", key: string, val: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };

    setMeta("property", "og:title", cleanTitle);
    setMeta("property", "og:description", metaDescription);
    setMeta("property", "og:image", heroImage);
    setMeta("property", "og:image:secure_url", heroImage);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:alt", cleanTitle);
    setMeta("property", "og:url", pageUrl);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "MetaFit Wellness");

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", cleanTitle);
    setMeta("name", "twitter:description", metaDescription);
    setMeta("name", "twitter:image", heroImage);
    setMeta("name", "twitter:image:alt", cleanTitle);
    setMeta("name", "description", metaDescription);
  }, [packageData, pageMetaTitle, cleanTitle, heroImage, metaDescription, pageUrl]);

  // Share Package handler (native mobile share sheet or clipboard copy)
  const [isCopied, setIsCopied] = useState(false);

  const handleSharePackage = async () => {
    const currentPrice = packageData?.price || 3499;
    const shareTitle = `${cleanTitle} | MetaFit Luxury Wellness Retreat`;
    const shareText = `Explore "${cleanTitle}" at MetaFit Wellness Retreat for ₹${currentPrice.toLocaleString("en-IN")}. Book your holistic rejuvenation experience:`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: pageUrl,
        });
        return;
      } catch (err) {
        // User dismissed native share sheet, fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(pageUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2500);
      toast.success("Package link copied! Ready to paste & share on WhatsApp or social platforms.", {
        duration: 3500,
        style: {
          background: "#15302B",
          color: "#FAF7F2",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: "16px",
          fontWeight: 600,
          fontSize: "13px",
        },
      });
    } catch (e) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2500);
      toast.success("Package link copied to clipboard!");
    }
  };

  // Helper to format 24-hour time string ("05:00", "22:00") into 12-hour AM/PM format ("05:00 AM", "10:00 PM")
  const format12HourTime = (time24?: string) => {
    if (!time24) return "";
    const clean = time24.trim();
    if (/am|pm/i.test(clean)) return clean;
    const [hStr, mStr] = clean.split(":");
    const h = parseInt(hStr, 10);
    if (isNaN(h)) return clean;
    const m = mStr ? mStr.padStart(2, "0") : "00";
    const period = h >= 12 ? "PM" : "AM";
    let hour = h % 12;
    if (hour === 0) hour = 12;
    return `${String(hour).padStart(2, "0")}:${m} ${period}`;
  };

  // ICON MAP for dynamic What's Included
  const THERAPY_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
    HeartHandshake,
    Sparkles,
    Flame,
    Droplets,
    Wind,
    Activity,
    Leaf,
    ShieldCheck,
    CheckCircle,
    Clock,
  };

  const getTherapyIcon = (iconName?: string) => {
    if (iconName && THERAPY_ICON_MAP[iconName]) return THERAPY_ICON_MAP[iconName];
    return Leaf;
  };

  // What's Included therapies (6 default therapies fallback)
  const defaultIncludedTherapies = [
    {
      title: "Full Body Massage",
      duration: "50 Minutes",
      description: "Relaxes muscles and improves circulation",
      icon: HeartHandshake,
      iconBg: "bg-[#E6F4EA]",
      iconColor: "text-[#137333]",
    },
    {
      title: "Shirodhara",
      duration: "50 Minutes",
      description: "Calms the mind and improves sleep",
      icon: Sparkles,
      iconBg: "bg-[#FEF3D6]",
      iconColor: "text-[#B06000]",
    },
    {
      title: "Far Infrared Detox",
      duration: "40 Minutes",
      description: "Supports natural detoxification",
      icon: Flame,
      iconBg: "bg-[#EAF3EC]",
      iconColor: "text-[#2D6A4F]",
    },
    {
      title: "Jal Neti",
      duration: null,
      description: "Cleanses nasal passage and improves breathing",
      icon: Droplets,
      iconBg: "bg-[#E8F0FE]",
      iconColor: "text-[#1A73E8]",
    },
    {
      title: "Pranayama",
      duration: null,
      description: "Enhances lung capacity and mental clarity",
      icon: Wind,
      iconBg: "bg-[#FCE8E6]",
      iconColor: "text-[#C5221F]",
    },
    {
      title: "Colon Hydrotherapy",
      duration: "40 Minutes",
      description: "Supports digestive health and internal cleansing",
      icon: Activity,
      iconBg: "bg-[#E6F4EA]",
      iconColor: "text-[#1E7E34]",
    },
  ];

  // Dynamic What's Included therapies from backend, falling back to defaults if not set
  const effectiveIncludedTherapies = useMemo(() => {
    if (packageData && "whatsIncluded" in packageData && packageData.whatsIncluded !== undefined && packageData.whatsIncluded !== null) {
      if (Array.isArray(packageData.whatsIncluded)) {
        if (packageData.whatsIncluded.length === 0) {
          // Explicitly deleted/empty in admin panel: hide What's Included section
          return [];
        }
        return packageData.whatsIncluded.map((item, idx) => ({
          title: item.title || `Therapy #${idx + 1}`,
          duration: item.duration || null,
          description: item.description || "",
          icon: getTherapyIcon(item.icon),
          iconBg: item.iconBg || "bg-[#E6F4EA]",
          iconColor: item.iconColor || "text-[#137333]",
        }));
      }
    }
    // Only fall back to defaults if whatsIncluded was never configured on this package
    return defaultIncludedTherapies;
  }, [packageData]);

  // Highlights
  const defaultHighlights = [
    "Certified Ayurvedic & Naturopathy Doctors and trained therapists",
    "100% Herbal, therapeutic-grade medicated oils and organic decoctions",
    "Personalised health & dosha consultation before beginning therapies",
    "Lakefront peaceful healing ambiance surrounded by pristine nature",
    "All detox kits, herbal teas, and fresh towels provided during session",
    "Private, hygienically sanitized therapy suites with natural light",
  ];

  // Dynamic Highlights from backend (checks highlights array, then includes array from Admin Panel)
  const effectiveHighlights = useMemo(() => {
    const rawHighlights = toArray(packageData?.highlights).filter(
      (h) => h && typeof h === "string" && h.trim()
    );
    const rawIncludes = toArray(packageData?.includes).filter(
      (i) => i && typeof i === "string" && i.trim()
    );

    if (rawHighlights.length > 0 && rawIncludes.length > 0) {
      const combined = [...rawHighlights];
      rawIncludes.forEach((item) => {
        if (!combined.includes(item)) combined.push(item);
      });
      return combined;
    }

    if (rawHighlights.length > 0) return rawHighlights;
    if (rawIncludes.length > 0) return rawIncludes;

    return defaultHighlights;
  }, [packageData?.highlights, packageData?.includes]);

  // Exclusions
  const defaultExclusions = [
    "Personal transportation to and from the retreat center",
    "Additional customized Ayurvedic treatments outside this package",
    "Specialized take-home herbal supplements and personal medicines",
  ];

  // Dynamic Exclusions from backend
  const effectiveExclusions = useMemo(() => {
    const rawExclusions = toArray(packageData?.exclusions).filter(
      (e) => e && typeof e === "string" && e.trim()
    );
    if (rawExclusions.length > 0) return rawExclusions;
    return defaultExclusions;
  }, [packageData?.exclusions]);

  // FAQs
  const defaultFaqs = [
    {
      question: "Who should take the Full Detox & Wellness Package?",
      answer:
        "This package is ideal for anyone feeling fatigued, stressed, experiencing sluggish digestion, or seeking a comprehensive physical and mental reboot through natural Ayurvedic and Naturopathy therapies.",
    },
    {
      question: "What should I wear or bring to the session?",
      answer:
        "Wear loose, comfortable clothing. Clean linen, disposable garments for therapies, towels, and herbal wash amenities are all provided for your comfort at the center.",
    },
    {
      question: "Is Colon Hydrotherapy safe for first-timers?",
      answer:
        "Yes, our Colon Hydrotherapy sessions are administered by qualified naturopathy practitioners using advanced, gentle, FDA-approved equipment with sterilized single-use applicators.",
    },
    {
      question: "Can I reschedule my appointment?",
      answer:
        "Yes, flexible rescheduling is supported up to 24 hours prior to your scheduled session time without any penalty.",
    },
    {
      question: "Are there any dietary precautions before the treatment?",
      answer:
        "We recommend eating a light, vegetarian meal at least 2 hours before your session and keeping yourself well-hydrated throughout the day.",
    },
  ];

  // Dynamic FAQs from backend
  const effectiveFaqs = useMemo(() => {
    const rawFaqs = toArray(packageData?.faqs).filter(
      (f) => f && f.question && f.question.trim()
    );
    if (rawFaqs.length > 0) return rawFaqs;
    return defaultFaqs;
  }, [packageData?.faqs]);

  if (loading) {
    return (
      <div className="flex min-h-[75vh] items-center justify-center bg-[#FAF7F2]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#15302B] border-t-transparent" />
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="flex min-h-[75vh] items-center justify-center bg-[#FAF7F2] px-4 py-12">
        <div className="max-w-md rounded-3xl border border-[#15302B]/10 bg-white p-8 text-center shadow-lg">
          <Leaf className="mx-auto h-10 w-10 text-[#3F6652]" />
          <h1 className="mt-4 font-serif text-2xl font-bold text-[#15302B]">
            Package Not Found
          </h1>
          <p className="mt-2 text-sm text-[#4A5568]">
            This wellness retreat package is unavailable or has been removed.
          </p>
          <Link
            to="/luxury-wellness-retreats"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#15302B] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#2C4A3B]"
          >
            Explore All Retreats
          </Link>
        </div>
      </div>
    );
  }

  const offerPrice = Number(packageData.price || 0);
  const rawOldPrice = Number(packageData.oldPrice || 0);
  const hasDiscount = rawOldPrice > offerPrice && offerPrice > 0;
  const oldPrice = hasDiscount ? rawOldPrice : null;
  const youSave = hasDiscount ? rawOldPrice - offerPrice : 0;
  const discountPercent = hasDiscount ? Math.round((youSave / rawOldPrice) * 100) : 0;

  const isAnyModalOpen =
    isBookingModalOpen ||
    isImageLightboxOpen ||
    selectedPhotoIndex !== null ||
    Boolean(activeVideo) ||
    Boolean(activePdf);

  const renderPriceComparisonBanner = () => {
    if (hasDiscount) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-lg border border-emerald-100 bg-emerald-50/50 p-2.5 text-[11px]">
          <div>
            <span className="text-slate-500 block text-[10px]">Actual Price</span>
            <span className="line-through text-slate-400 font-semibold">{formatCurrency(oldPrice!)}</span>
          </div>
          <div>
            <span className="text-emerald-700 block text-[10px] font-medium">Offer Price</span>
            <span className="font-bold text-slate-900">{formatCurrency(offerPrice)}</span>
          </div>
          <div>
            <span className="text-emerald-700 block text-[10px] font-medium">You Save</span>
            <span className="font-bold text-emerald-700">{formatCurrency(youSave)} ({discountPercent}% OFF)</span>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-[11px]">
        <div>
          <span className="text-slate-500 block text-[10px]">Package Price</span>
          <span className="font-bold text-slate-900">{formatCurrency(offerPrice)}</span>
        </div>
        <span className="rounded bg-emerald-100/70 text-emerald-800 text-[10px] font-semibold px-2 py-0.5">
          All-Inclusive
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased pb-24">
      <Toaster position="top-center" reverseOrder={false} />

      {/* Dynamic SEO & Social OpenGraph Card for WhatsApp, Facebook, Twitter, LinkedIn, etc. */}
      <Helmet>
        <title>{pageMetaTitle}</title>
        <meta name="title" content={pageMetaTitle} />
        <meta name="description" content={metaDescription} />

        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="MetaFit Wellness" />
        <meta property="og:title" content={cleanTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={heroImage} />
        <meta property="og:image:secure_url" content={heroImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={cleanTitle} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={cleanTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:image:alt" content={cleanTitle} />
      </Helmet>

      {/* ============================================================== */}
      {/* MAIN CONTENT (MATCHING BOOKMYADVENTURE UI)                     */}
      {/* ============================================================== */}
      <div className="max-w-4xl mx-auto px-3 sm:px-5 space-y-2" style={{ paddingTop: "8px" }}>
        {/* 1. HERO POSTER / BANNER CARD */}
        <div className="w-full mt-0 mb-2 rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-2xs">
          <div
            onClick={() => setIsImageLightboxOpen(true)}
            className="relative w-full rounded-2xl overflow-hidden group cursor-pointer"
          >
            <img
              src={heroImage}
              alt={cleanTitle || "Retreat Package Flyer"}
              className="w-full h-auto block relative z-0 transition-transform duration-500 group-hover:scale-[1.01]"
            />
            {/* Top Left Location Badge */}
            <div className="absolute top-2.5 left-2.5 flex items-center gap-2 z-10">
              <div className="flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-md shadow-md">
                <MapPin className="h-3 w-3 text-[#FF6B35]" />
                <span>
                  {packageData.city
                    ? `${packageData.city}${packageData.state ? `, ${packageData.state}` : ""}`
                    : (packageData.TreatmentOrganiser?.trim() || "Wellness Retreat")}
                </span>
              </div>
            </div>

            {/* Top Right Enlarge */}
            <div className="absolute top-2.5 right-2.5 opacity-90 group-hover:opacity-100 transition-opacity z-10">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-medium text-white backdrop-blur-md shadow-sm">
                <Maximize2 className="h-3 w-3" />
                <span>Enlarge</span>
              </span>
            </div>
          </div>
        </div>

        {/* 2. TITLE & ORGANIZER CARD */}
        <div className="bg-white rounded-xl p-2 sm:p-2.5 border border-slate-200 shadow-2xs">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h1 className="text-[13.5px] sm:text-[14.5px] font-bold text-slate-900 leading-tight">
                {cleanTitle}
              </h1>
            </div>
            <button
              type="button"
              onClick={handleSharePackage}
              className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-800 transition-all flex-shrink-0 cursor-pointer shadow-2xs"
              title="Share Package"
              aria-label="Share Package"
            >
              {isCopied ? (
                <Check className="w-3 h-3 text-emerald-600" />
              ) : (
                <Share2 className="w-3 h-3" />
              )}
            </button>
          </div>
          <div className="pt-0.5 text-[10.5px] leading-tight font-normal space-y-0.5">
            {packageData.TreatmentOrganiser?.trim() && (
              <div className="flex items-center gap-1.5">
                <UserCheck className="w-[11px] h-[11px] text-slate-900 flex-shrink-0" />
                <span className="text-slate-900 font-medium">
                  {packageData.TreatmentOrganiser.trim()}
                </span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <MapPin className="w-[11px] h-[11px] text-slate-900 flex-shrink-0" />
              <span className="text-slate-900 flex items-center gap-1">
                {[packageData.address, packageData.city, packageData.state]
                  .filter(Boolean)
                  .join(", ") || "Rajsamand, Rajasthan"}
                {packageData.googleMapUrl && (
                  <a
                    href={packageData.googleMapUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#FF6B35] hover:text-[#e85a28] inline-flex items-center ml-0.5"
                    title="View on Maps"
                  >
                    <ExternalLink className="w-[11px] h-[11px]" />
                  </a>
                )}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-[11px] h-[11px] text-slate-900 flex-shrink-0" />
              <span className="text-slate-900">
                {packageData.bookingType ? `${packageData.bookingType} • ` : "Daily • "}
                {packageData.startTime && packageData.endTime
                  ? `${format12HourTime(packageData.startTime)} to ${format12HourTime(packageData.endTime)} (IST)`
                  : "06:00 AM to 09:00 PM (IST)"}
                {packageData.duration ? ` · ${packageData.duration}` : ""}
              </span>
            </div>
          </div>
        </div>

        {/* 3. HIGHLIGHTS CARD */}
        <div className="bg-white rounded-xl p-2.5 sm:p-3 border border-slate-200 shadow-2xs">
          <div>
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight mb-1">
              Highlights
            </h2>
            {/* Mobile: single stacked list with compact spacing */}
            <div className="sm:hidden space-y-1">
              {effectiveHighlights.map((hl, idx) => (
                <div key={idx} className="flex items-start gap-1.5 py-0 px-0.5 bg-transparent group">
                  <span className="text-slate-900 text-xs leading-none mt-0.5 select-none">•</span>
                  <p className="text-slate-900 font-normal text-[11px] leading-tight">{hl}</p>
                </div>
              ))}
            </div>

            {/* Desktop: 2 independent columns so longer items in one column do not create empty gaps in the other */}
            <div className="hidden sm:grid sm:grid-cols-2 gap-x-6">
              <div className="space-y-1">
                {effectiveHighlights
                  .filter((_, idx) => idx % 2 === 0)
                  .map((hl, idx) => (
                    <div key={idx} className="flex items-start gap-1.5 py-0 px-0.5 bg-transparent group">
                      <span className="text-slate-900 text-xs leading-none mt-0.5 select-none">•</span>
                      <p className="text-slate-900 font-normal text-[11px] leading-tight">{hl}</p>
                    </div>
                  ))}
              </div>
              <div className="space-y-1">
                {effectiveHighlights
                  .filter((_, idx) => idx % 2 !== 0)
                  .map((hl, idx) => (
                    <div key={idx} className="flex items-start gap-1.5 py-0 px-0.5 bg-transparent group">
                      <span className="text-slate-900 text-xs leading-none mt-0.5 select-none">•</span>
                      <p className="text-slate-900 font-normal text-[11px] leading-tight">{hl}</p>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* 4. GALLERY */}
        {galleryItems.length > 0 && (
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-2xs transition-all">
            <button
              type="button"
              onClick={() => setIsGalleryOpen(!isGalleryOpen)}
              className="w-full px-3.5 py-2.5 flex justify-between items-center bg-transparent hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight">
                  Gallery
                </h2>
                <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 text-slate-600 font-medium">
                  {galleryItems.length}
                </span>
              </div>
              <div className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0">
                <ChevronDown
                  className={`h-3 w-3 transition-transform duration-200 ${
                    isGalleryOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>
            {isGalleryOpen && (
              <div className="px-3.5 pb-3.5 pt-2 border-t border-slate-100">
                <div className="flex flex-wrap items-center gap-1.5 pb-2.5 border-b border-slate-100">
                  <button
                    type="button"
                    onClick={() => setActiveGalleryTab("all")}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold transition-all cursor-pointer ${
                      activeGalleryTab === "all"
                        ? "bg-slate-900 text-white"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    All Media ({galleryItems.length})
                  </button>
                  {photoCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveGalleryTab("photo")}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold transition-all cursor-pointer ${
                        activeGalleryTab === "photo"
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Photos ({photoCount})
                    </button>
                  )}
                  {videoCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveGalleryTab("video")}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold transition-all cursor-pointer ${
                        activeGalleryTab === "video"
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Videos ({videoCount})
                    </button>
                  )}
                  {pdfCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveGalleryTab("pdf")}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold transition-all cursor-pointer ${
                        activeGalleryTab === "pdf"
                          ? "bg-slate-900 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      PDFs ({pdfCount})
                    </button>
                  )}
                </div>

                {/* 1 SINGLE ROW AUTO-SLIDING GALLERY CAROUSEL */}
                <div
                  className="relative mt-2.5 group"
                  onMouseEnter={() => setIsGalleryHovered(true)}
                  onMouseLeave={() => setIsGalleryHovered(false)}
                >
                  {/* Left arrow scroll */}
                  <button
                    type="button"
                    onClick={() => scrollGallery("left")}
                    className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-md border border-slate-200 flex items-center justify-center cursor-pointer transition-opacity opacity-0 group-hover:opacity-100"
                    aria-label="Previous images"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {/* Horizontal 1-Row Track */}
                  <div
                    ref={galleryScrollRef}
                    className="flex flex-nowrap items-center gap-2.5 overflow-x-auto scroll-auto py-1 px-0.5 select-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                  >
                    {filteredGallery.map((item, idx) => {
                      const isVideo = item.type === "video";
                      const isPdf = item.type === "pdf";

                      if (isPdf) {
                        return (
                          <div
                            key={item.id || idx}
                            className="relative w-40 sm:w-48 h-28 sm:h-32 flex-shrink-0 rounded-lg border border-rose-100 bg-rose-50/40 p-2.5 flex flex-col justify-between shadow-2xs"
                          >
                            <div>
                              <FileText className="h-4 w-4 text-rose-500 mb-1" />
                              <p className="text-[11px] font-semibold text-slate-800 line-clamp-2 leading-tight">
                                {item.title}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setActivePdf(item)}
                              className="mt-1 text-[10px] font-bold text-rose-700 underline text-left cursor-pointer"
                            >
                              View PDF
                            </button>
                          </div>
                        );
                      }

                      if (isVideo) {
                        return (
                          <div
                            key={item.id || idx}
                            onClick={() => setActiveVideo(item)}
                            className="relative w-40 sm:w-48 h-28 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden bg-slate-900 cursor-pointer group/vid shadow-2xs"
                          >
                            <img
                              src={item.thumbnail || heroImage}
                              alt={item.title}
                              className="w-full h-full object-cover opacity-80 group-hover/vid:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                              <Play className="h-6 w-6 text-white drop-shadow-md" />
                            </div>
                          </div>
                        );
                      }

                      const photoIndex = photoItemsOnly.findIndex((p) => p.id === item.id);
                      return (
                        <div
                          key={item.id || idx}
                          onClick={() => setSelectedPhotoIndex(photoIndex >= 0 ? photoIndex : 0)}
                          className="relative w-40 sm:w-48 h-28 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100 cursor-pointer group/img shadow-2xs"
                        >
                          <img
                            src={item.url}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-300"
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Right arrow scroll */}
                  <button
                    type="button"
                    onClick={() => scrollGallery("right")}
                    className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-md border border-slate-200 flex items-center justify-center cursor-pointer transition-opacity opacity-0 group-hover:opacity-100"
                    aria-label="Next images"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 5. ABOUT THIS PACKAGE CARD */}
        <div className="bg-white rounded-xl p-3 sm:p-3.5 border border-slate-200 shadow-2xs">
          <div>
            <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 mb-2">
              About This Package
            </h2>
            <div>
              <div className="relative">
                <div
                  className={`prose prose-slate max-w-none text-slate-700 leading-relaxed text-xs sm:text-sm text-[12px] break-words transition-all duration-300 ${
                    !isSummaryExpanded ? "max-h-24 overflow-hidden" : ""
                  }`}
                >
                  {packageData?.description && packageData.description.trim() ? (
                    renderDescriptionContent(packageData.description)
                  ) : (
                    <p>
                      Our Full Detox &amp; Wellness Package is designed to cleanse your body, calm
                      your mind, and restore inner balance. Experience a blend of traditional
                      wellness therapies and modern care in a peaceful, nature-filled environment.
                    </p>
                  )}
                </div>
                {!isSummaryExpanded && (
                  <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none" />
                )}
              </div>
              <div className="mt-2 flex justify-start">
                <button
                  type="button"
                  onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-black hover:text-black transition-all cursor-pointer shadow-2xs underline"
                >
                  <span>{isSummaryExpanded ? "See Less" : "See More"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 5. WHAT'S INCLUDED & WHAT'S NOT INCLUDED ACCORDIONS */}
        <div className="space-y-2">
          {/* What's Included */}
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-2xs transition-all">
            <button
              type="button"
              onClick={() => setIsIncludedOpen(!isIncludedOpen)}
              className="w-full px-3.5 py-2.5 flex justify-between items-center bg-transparent hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight">
                  What&#39;s Included
                </h2>
              </div>
              <div className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0">
                <ChevronDown
                  className={`h-3 w-3 transition-transform duration-200 ${
                    isIncludedOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>
            {isIncludedOpen && (
              <div className="px-3.5 pb-3 pt-1 border-t border-slate-100">
                {effectiveIncludedTherapies.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-700">
                    {effectiveIncludedTherapies.map((therapy, idx) => (
                      <div key={idx} className="flex items-start gap-2 py-0.5">
                        <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5 stroke-[2.5]" />
                        <div>
                          <span className="font-semibold text-slate-900">{therapy.title}</span>
                          {therapy.duration && (
                            <span className="text-[10px] text-[#FF6B35] font-medium ml-1">
                              ({therapy.duration})
                            </span>
                          )}
                          {therapy.description && (
                            <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                              {therapy.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-700">
                    {effectiveHighlights.slice(0, 4).map((hl, idx) => (
                      <div key={idx} className="flex items-start gap-2 py-0.5">
                        <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5 stroke-[2.5]" />
                        <span>{hl}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 pt-2.5 border-t border-slate-100">
                  {renderPriceComparisonBanner()}
                </div>
              </div>
            )}
          </div>

          {/* What's Not Included */}
          <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-2xs transition-all">
            <button
              type="button"
              onClick={() => setIsNotIncludedOpen(!isNotIncludedOpen)}
              className="w-full px-3.5 py-2.5 flex justify-between items-center bg-transparent hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight">
                  What&#39;s Not Included
                </h2>
              </div>
              <div className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0">
                <ChevronDown
                  className={`h-3 w-3 transition-transform duration-200 ${
                    isNotIncludedOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>
            {isNotIncludedOpen && (
              <div className="px-3.5 pb-3 pt-1 border-t border-slate-100">
                <div className="space-y-1.5 text-[11px] text-slate-700">
                  {effectiveExclusions.map((ex, idx) => (
                    <div key={idx} className="flex items-start gap-2 py-0.5">
                      <X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5 stroke-[2.5]" />
                      <span>{ex}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 8. FAQS ACCORDION */}
        <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-2xs transition-all">
          <button
            type="button"
            onClick={() => setIsFaqSectionOpen(!isFaqSectionOpen)}
            className="w-full px-3.5 py-2.5 flex justify-between items-center bg-transparent hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight">
                FAQs
              </h2>
            </div>
            <div className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0">
              <ChevronDown
                className={`h-3 w-3 transition-transform duration-200 ${
                  isFaqSectionOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>
          {isFaqSectionOpen && (
            <div className="px-3.5 pb-3.5 pt-2 border-t border-slate-100 divide-y divide-slate-100 text-[11px]">
              {effectiveFaqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div key={idx} className="py-2 first:pt-0 last:pb-0">
                    <button
                      type="button"
                      onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                      className="flex w-full items-center justify-between text-left font-semibold text-slate-900 hover:text-black cursor-pointer py-1"
                    >
                      <span>{faq.question}</span>
                      {isOpen ? (
                        <ChevronUp className="h-3.5 w-3.5 shrink-0 text-slate-600" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      )}
                    </button>
                    {isOpen && (
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-600 pl-1">
                        {faq.answer}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 10. LOCATION & DIRECTIONS */}
        <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-2xs transition-all">
          <button
            type="button"
            onClick={() => setIsLocationOpen(!isLocationOpen)}
            className="w-full px-3.5 py-2.5 flex justify-between items-center bg-transparent hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <h2 className="text-[12px] sm:text-[14px] font-semibold text-slate-900 tracking-tight">
                Location &amp; Directions
              </h2>
            </div>
            <div className="w-6 h-6 rounded-full flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors flex-shrink-0">
              <ChevronDown
                className={`h-3 w-3 transition-transform duration-200 ${
                  isLocationOpen ? "rotate-180" : ""
                }`}
              />
            </div>
          </button>
          {isLocationOpen && (
            <div className="px-3.5 pb-3.5 pt-2 border-t border-slate-100 space-y-2">
              <p className="text-[11px] text-slate-600">
                {[packageData.address, packageData.city, packageData.state].filter(Boolean).join(", ") ||
                  "Udaipur, Rajasthan, India"}
              </p>
              <div className="h-[200px] w-full rounded-lg overflow-hidden bg-slate-100">
                <iframe
                  src={
                    packageData.googleMapEmbedUrl ||
                    (packageData.address || packageData.city
                      ? `https://maps.google.com/maps?q=${encodeURIComponent(
                          [packageData.address, packageData.city, packageData.state]
                            .filter(Boolean)
                            .join(", ")
                        )}&z=15&output=embed`
                      : "https://maps.google.com/maps?q=India&z=5&output=embed")
                  }
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  title={`${packageData.packageName || "Wellness Retreat"} Location Map`}
                />
              </div>
            </div>
          )}
        </div>

        {/* 11. OTHER PACKAGES (MATCHING BOOKMYADVENTURE) */}
        {otherPackages.length > 0 && (
          <div className="bg-white rounded-xl p-3.5 sm:p-4 border border-slate-200 shadow-2xs">
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
              <div>
                <h2 className="text-[13px] sm:text-[15px] font-bold text-slate-900 tracking-tight">
                  Other Packages
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Explore more wellness retreats &amp; naturopathy stays
                </p>
              </div>
              <Link
                to="/luxury-wellness-retreats"
                className="text-[11px] font-semibold text-[#FF6B35] hover:underline inline-flex items-center gap-1"
              >
                <span>View All</span>
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {otherPackages.slice(0, 6).map((pkg) => {
                const pkgPhotos = toArray(pkg.uploadImages);
                const pkgImg =
                  pkgPhotos[0] ||
                  (pkg.gallery && pkg.gallery[0]?.url) ||
                  heroImage;
                const pkgOfferPrice = pkg.price || 3500;
                const pkgOldPrice = pkg.oldPrice;
                const pkgHasDiscount = pkgOldPrice && pkgOldPrice > pkgOfferPrice;

                return (
                  <Link
                    key={pkg._id}
                    to={`/luxury-wellness-retreats/${pkg._id}`}
                    className="group bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer flex flex-col w-full"
                  >
                    {/* Flyer / Thumbnail Header */}
                    <div className="relative aspect-[16/10] bg-slate-100 overflow-hidden w-full">
                      <img
                        src={pkgImg}
                        alt={pkg.packageName || "Retreat Package"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {pkg.duration && (
                        <div className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-xs text-white text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 z-20 border border-white/10 shadow-xs">
                          <Clock className="h-3 w-3 text-[#FF6B35]" />
                          <span>{pkg.duration}</span>
                        </div>
                      )}
                    </div>

                    {/* Card Body */}
                    <div className="p-3.5 sm:p-4 flex flex-col bg-white">
                      {/* Title */}
                      <h3 className="font-bold text-[14px] sm:text-[15px] text-slate-900 line-clamp-1 group-hover:text-[#FF6B35] transition-colors leading-snug">
                        {pkg.packageName || "Wellness Retreat"}
                      </h3>

                      {/* Location with Orange MapPin */}
                      <div className="flex items-center gap-1.5 text-[11px] sm:text-[12px] text-slate-500 mt-1">
                        <MapPin className="w-3.5 h-3.5 text-[#FF6B35] shrink-0 fill-[#FF6B35]/20" />
                        <span className="truncate">
                          {[pkg.city, pkg.state].filter(Boolean).join(", ") || "India"}
                        </span>
                      </div>

                      {/* Price Row with Ticket icon - line removed & compact spacing */}
                      <div className="flex items-center gap-1.5 text-slate-500 mt-1">
                        <Ticket className="w-3 h-3 text-slate-400 shrink-0" />
                        <span className="text-slate-800 font-semibold text-[11px] sm:text-[12px]">{formatOnwardsPrice(pkgOfferPrice)}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ============================================================== */}
      {/* 3. BOOKING COUNTER MODAL (Full Booking Selection Preserved)    */}
      {/* ============================================================== */}
      {isBookingModalOpen && packageData && (
        <div
          onClick={closeBookingModal}
          className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-black/60 backdrop-blur-xs p-4 pt-20 pb-8 sm:p-6"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md my-auto flex flex-col max-h-[calc(100vh-5.5rem)] rounded-3xl border border-[#15302B]/15 bg-white shadow-2xl animate-fade-in-up overflow-hidden"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-[#15302B]/8 px-5 py-4 sm:px-6 sm:py-4.5 bg-[#FAF7F2] shrink-0">
              <div>
                <h3 className="font-serif text-lg sm:text-xl font-bold text-[#15302B]">
                  Book Package
                </h3>
                <p className="text-[11px] text-[#4A5568]">
                  Select participants for this session
                </p>
              </div>
              <button
                type="button"
                onClick={closeBookingModal}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-black/5 text-[#4A5568] transition-colors hover:bg-black/10 cursor-pointer"
                aria-label="Close modal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="space-y-4 p-5 sm:p-6 overflow-y-auto flex-1">
              {/* Selected package pill */}
              <div className="rounded-2xl border border-[#15302B]/10 bg-[#FAF7F2] p-3">
                <h4 className="text-xs font-bold text-[#15302B]">{cleanTitle}</h4>
                <p className="text-[10px] text-[#4A5568] mt-0.5">
                  {[packageData.TreatmentOrganiser?.trim(), packageData.city, packageData.state]
                    .filter(Boolean)
                    .join(" · ") || "Wellness Retreat"}
                </p>
              </div>

              {/* Options selection */}
              <div className="max-h-[220px] sm:max-h-[260px] space-y-2.5 overflow-y-auto pr-1">
                {toArray(packageData.pricingOptions).length > 0 ? (
                  toArray(packageData.pricingOptions).map((option) => {
                    const quantity = selectedQuantities[option.type] ?? 0;
                    return (
                      <div
                        key={option.type}
                        className="flex items-center justify-between rounded-xl border border-[#15302B]/8 bg-white p-3 shadow-2xs"
                      >
                        <div className="pr-2">
                          <p className="text-xs font-semibold text-[#15302B]">
                            {option.type}
                          </p>
                          <p className="text-[11px] text-[#4A5568]">
                            {formatCurrency(option.price)} per person
                          </p>
                        </div>
                        <div className="flex items-center gap-2 rounded-full border border-[#15302B]/15 bg-white p-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => updateQuantity(option.type, -1)}
                            className="flex h-6 w-6 items-center justify-center rounded-full text-[#15302B] hover:bg-[#15302B]/8 cursor-pointer"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="min-w-[1.2rem] text-center font-mono text-xs font-bold">
                            {quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(option.type, 1)}
                            className="flex h-6 w-6 items-center justify-center rounded-full text-[#15302B] hover:bg-[#15302B]/8 cursor-pointer"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center justify-between rounded-xl border border-[#15302B]/8 bg-white p-3 shadow-2xs">
                    <div className="pr-2">
                      <p className="text-xs font-semibold text-[#15302B]">
                        Standard Participant
                      </p>
                      <p className="text-[11px] text-[#4A5568]">
                        {formatCurrency(packageData.price || 3500)} per person
                      </p>
                    </div>
                    <div className="flex items-center gap-2 rounded-full border border-[#15302B]/15 bg-white p-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => updateQuantity("Standard", -1)}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-[#15302B] hover:bg-[#15302B]/8 cursor-pointer"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="min-w-[1.2rem] text-center font-mono text-xs font-bold">
                        {selectedQuantities["Standard"] || 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => updateQuantity("Standard", 1)}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-[#15302B] hover:bg-[#15302B]/8 cursor-pointer"
                        aria-label="Increase quantity"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Total Calculation */}
              {(() => {
                const base = getBookingBaseTotal(packageData, selectedQuantities);
                const rate = getCommissionRate(packageData);
                const commFee = getBookingCommissionAmount(packageData, selectedQuantities);
                const total = Math.round((base + commFee) * 100) / 100;
                return (
                  <div className="space-y-1.5 rounded-xl border border-[#2D6A4F]/20 bg-[#F2F8F4] p-3 text-xs">
                    <div className="flex items-center justify-between text-[#4A5568]">
                      <span>Base Amount:</span>
                      <span className="font-mono font-medium text-[#15302B]">{formatCurrency(base)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-[#4A5568]">Taxes & Platform Fees:</span>
                      {rate > 0 ? (
                        <span className="font-semibold text-[#2D6A4F]">
                          Included{commFee > 0 ? ` (+${formatCurrency(commFee)})` : ''}
                        </span>
                      ) : (
                        <span className="font-semibold text-[#B91C1C]">
                          Not Included
                        </span>
                      )}
                    </div>
                    <div className="border-t border-[#2D6A4F]/15 pt-1.5 flex items-center justify-between font-bold">
                      <span className="text-[#15302B]">Estimated Total:</span>
                      <span className="font-mono text-sm text-[#137333]">{formatCurrency(total)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Action Buttons */}
              <div className="flex gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={closeBookingModal}
                  className="flex-1 rounded-full border border-[#15302B]/20 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-[#15302B] transition-colors hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={getBookingTotal(packageData, selectedQuantities) <= 0}
                  onClick={handleContinueToPreview}
                  className="flex-1 rounded-full bg-black hover:bg-neutral-800 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-white shadow-sm transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                  Continue to Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* 4. PHOTO LIGHTBOX (Full Resolution Image Carousel)             */}
      {/* ============================================================== */}
      {selectedPhotoIndex !== null && photoItemsOnly[selectedPhotoIndex] && (
        <div
          onClick={() => setSelectedPhotoIndex(null)}
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-pointer"
        >
          <button
            type="button"
            onClick={() => setSelectedPhotoIndex(null)}
            className="absolute top-5 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Previous Button */}
          {photoItemsOnly.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPhotoIndex((prev) => (prev! > 0 ? prev! - 1 : photoItemsOnly.length - 1));
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-all cursor-pointer"
              aria-label="Previous image"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
          )}

          {/* Next Button */}
          {photoItemsOnly.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPhotoIndex((prev) => (prev! < photoItemsOnly.length - 1 ? prev! + 1 : 0));
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-all cursor-pointer"
              aria-label="Next image"
            >
              <span className="rotate-180 inline-block">
                <ArrowLeft className="h-6 w-6" />
              </span>
            </button>
          )}

          {/* Image Container */}
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[85vh] max-w-4xl overflow-hidden rounded-2xl bg-black p-2 shadow-2xl flex flex-col items-center"
          >
            <img
              src={photoItemsOnly[selectedPhotoIndex].url}
              alt={photoItemsOnly[selectedPhotoIndex].title}
              className="max-h-[75vh] w-auto max-w-full object-contain rounded-xl"
            />
            <div className="mt-3 flex items-center justify-between w-full px-3 text-white">
              <div>
                <h4 className="text-sm font-bold">{photoItemsOnly[selectedPhotoIndex].title}</h4>
                {photoItemsOnly[selectedPhotoIndex].description && (
                  <p className="text-xs text-white/70">{photoItemsOnly[selectedPhotoIndex].description}</p>
                )}
              </div>
              <a
                href={photoItemsOnly[selectedPhotoIndex].url}
                target="_blank"
                rel="noreferrer"
                download
                className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold hover:bg-white/30 transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                <span>Save</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* 5. VIDEO PLAYER MODAL                                          */}
      {/* ============================================================== */}
      {activeVideo && (
        <div
          onClick={() => setActiveVideo(null)}
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-pointer"
        >
          <button
            type="button"
            onClick={() => setActiveVideo(null)}
            className="absolute top-5 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-black shadow-2xl"
          >
            <div className="aspect-video w-full bg-black">
              {activeVideo.url.includes("youtube.com") || activeVideo.url.includes("youtu.be") ? (
                <iframe
                  src={
                    activeVideo.url.includes("watch?v=")
                      ? activeVideo.url.replace("watch?v=", "embed/")
                      : activeVideo.url
                  }
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={activeVideo.title}
                />
              ) : (
                <video
                  src={activeVideo.url}
                  controls
                  autoPlay
                  className="h-full w-full object-contain"
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* 6. PDF VIEWER / DOCUMENT MODAL                                 */}
      {/* ============================================================== */}
      {activePdf && (
        <div
          onClick={() => setActivePdf(null)}
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/85 backdrop-blur-md p-3 sm:p-6 cursor-pointer"
        >
          {/* Side Floating Close Button (Top-Right) */}
          <button
            type="button"
            onClick={() => setActivePdf(null)}
            className="fixed top-4 right-4 md:top-6 md:right-6 z-[1200] flex h-11 w-11 items-center justify-center rounded-full bg-white/25 text-white backdrop-blur-md hover:bg-white/40 transition-all cursor-pointer shadow-2xl border border-white/30 hover:scale-105 active:scale-95"
            aria-label="Close PDF Viewer"
            title="Close"
          >
            <X className="h-6 w-6 text-white" />
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-[96vw] max-w-6xl h-[92vh] flex flex-col rounded-3xl bg-white shadow-2xl overflow-hidden border border-white/20 cursor-default"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-[#15302B]/10 px-5 sm:px-7 py-3.5 sm:py-4 bg-[#FAF7F2]">
              <div className="flex items-center gap-3 min-w-0 pr-2">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600 border border-rose-200">
                  <FileText className="h-5 w-5 text-rose-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-serif text-base sm:text-lg font-bold text-[#15302B] truncate">
                    {activePdf.title}
                  </h3>
                  <span className="text-[11px] sm:text-xs text-[#4A5568]">
                    Official Wellness PDF Guide & Brochure
                  </span>
                </div>
              </div>

              {/* Actions: Download, Full View, Close */}
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                {/* Download PDF Button with high-contrast, crystal clear styling */}
                <a
                  href={activePdf.url}
                  download={activePdf.title || "wellness-package-document.pdf"}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 px-4 py-2 text-xs font-bold text-white shadow-md hover:shadow-lg transition-all cursor-pointer active:scale-95"
                  style={{ backgroundColor: "#15803d", color: "#ffffff" }}
                >
                  <Download className="h-4 w-4" style={{ color: "#ffffff", stroke: "#ffffff" }} />
                  <span style={{ color: "#ffffff", fontWeight: 700 }}>Download PDF</span>
                </a>

                {/* External Full Screen Link */}
                <a
                  href={activePdf.url}
                  target="_blank"
                  rel="noreferrer"
                  className="hidden md:inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-[#15302B] shadow-xs hover:bg-slate-100 transition-colors cursor-pointer"
                  title="Open in new window"
                >
                  <ExternalLink className="h-3.5 w-3.5 text-slate-600" />
                  <span>Full View</span>
                </a>

                {/* Header Close Button */}
                <button
                  type="button"
                  onClick={() => setActivePdf(null)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-xs hover:bg-slate-100 hover:text-slate-900 transition-colors cursor-pointer"
                  aria-label="Close"
                >
                  <X className="h-4 w-4 text-slate-600" />
                  <span className="hidden sm:inline">Close</span>
                </button>
              </div>
            </div>

            {/* Embedded PDF iframe / View Area */}
            <div className="flex-1 h-full w-full bg-slate-200/60 min-h-0 relative">
              <iframe
                src={`${activePdf.url}#toolbar=1`}
                className="h-full w-full border-0"
                title={activePdf.title}
              />
            </div>
          </div>
        </div>
      )}


      {/* ============================================================== */}
      {/* 8. SINGLE IMAGE LIGHTBOX (Image 3 Zoom)                        */}
      {/* ============================================================== */}
      {isImageLightboxOpen && (
        <div
          onClick={() => setIsImageLightboxOpen(false)}
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 cursor-pointer"
        >
          <button
            type="button"
            onClick={() => setIsImageLightboxOpen(false)}
            className="absolute top-5 right-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <X className="h-6 w-6" />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-4xl overflow-hidden rounded-2xl bg-white p-2 shadow-2xl"
          >
            <img
              src={heroImage}
              alt="Enlarged Package Flyer"
              className="max-h-[85vh] w-auto object-contain mx-auto rounded-xl"
            />
          </div>
        </div>
      )}

      {/* ============================================================== */}
      {/* 9. FIXED BOTTOM BOOKING BAR (MATCHING BOOKMYADVENTURE)         */}
      {/* ============================================================== */}
      {!isAnyModalOpen && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/90 py-3 px-4 sm:px-8 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 sm:gap-4">
            {/* Price Info */}
            <div className="flex flex-col min-w-0">
              <span className="text-[9px] sm:text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-none mb-0.5">
                Starting from
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-base sm:text-[14px] font-semibold text-[#FF6B35] leading-tight">
                  {formatCurrency(offerPrice)}
                </span>
                <span className="text-[10px] sm:text-[11px] text-slate-500 font-medium">
                  / {packageData.bookingType?.toLowerCase().includes("slot") ? "slot" : "person"}
                </span>
                {hasDiscount && (
                  <span className="text-[10px] sm:text-[11px] line-through text-slate-400 ml-1">
                    {formatCurrency(oldPrice!)}
                  </span>
                )}
                {hasDiscount && (
                  <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded ml-1">
                    {discountPercent}% OFF
                  </span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center flex-shrink-0">
              <button
                type="button"
                onClick={openBookingModal}
                className="px-6 py-2.5 bg-black hover:bg-neutral-800 text-white rounded-full font-semibold text-[13px] sm:text-[14px] transition-all shadow-sm active:scale-95 cursor-pointer"
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WellnessAndNaturopathyDetailPage;
